import { twMerge } from "tailwind-merge";

import type { StartedFeatureName } from "@/api/splashpages";
import { Badge } from "@/design-system/components/Badge/Badge";

import { useNewBadge } from "./useNewBadge";

export interface NewBadgeProps {
  // Identity of the feature this badge belongs to. Matched against the
  // `started_features` records so the badge clears once the user clicks
  // "Get Started". Omit for a badge whose interaction gate is a local
  // dismissal (see `storageKey`) or that has no interaction gate at all.
  featureKey?: StartedFeatureName;
  // UTC `Date` marking when the feature became "new", built from an explicit
  // ISO-8601 string with a `Z` offset (e.g. `new Date("2026-06-17T00:00:00Z")`)
  // so the cutoff is unambiguous. Omit (or backdate) to retire the badge with
  // no other code changes.
  newSince?: Date;
  // Days the badge stays visible after `newSince` before the time gate closes.
  ttlDays?: number;
  // Stable key (e.g. a nav item's href or a page path) used to persist
  // dismissal for badges with no `featureKey`. Dismiss it with
  // `dismissNewBadge(storageKey)`. Ignored when `featureKey` is set.
  storageKey?: string;
  className?: string;
  variant?: keyof typeof variantSoftStyles;
}

const variantSoftStyles = {
  success: "bg-status-success-tint text-status-success",
  warning: "bg-status-warning-tint text-status-warning",
  error: "bg-status-error-tint text-status-error",
  unknown: "bg-surface-hover text-textSubtle",
  info: "bg-status-info-tint text-status-info",
};

export function NewBadge({
  featureKey,
  newSince,
  ttlDays,
  storageKey,
  className,
  variant = "success",
}: NewBadgeProps) {
  const visible = useNewBadge(featureKey, newSince, ttlDays, storageKey);

  if (!visible) return null;

  return (
    <Badge
      text="New"
      className={twMerge(className, "mr-1", variantSoftStyles[variant])}
    />
  );
}
